import {
  Directive,
  HostBinding,
  HostListener,
  Input,
  ElementRef,
} from '@angular/core';

@Directive({
  selector: '[validateDecimalNumber]',
})
export class ValidateDecimalNumberDirective {
  @HostBinding('autocomplete') public autocomplete = 'off';

  @Input() public decimalPlaces?: number = 2;

  constructor(private el: ElementRef) {}

  @HostListener('keypress', ['$event']) public onKeyPress(
    event: KeyboardEvent
  ) {
    const input = this.el.nativeElement;
    const keyCode = event.keyCode || event.which;
    const keyValue = String.fromCharCode(keyCode);

    // Allow control keys like backspace, delete, arrow keys, etc.
    if (this.isControlKey(event)) {
      return true;
    }

    // Allow one minus sign at the start if the input is empty
    if (
      keyValue === '-' &&
      input.selectionStart === 0 &&
      input.value.indexOf('-') === -1
    ) {
      return true;
    }

    // Treat a comma as a decimal point and insert a dot instead
    if (keyValue === ',') {
      event.preventDefault();
      if (input.value.indexOf('.') === -1) {
        this.insertDecimalPoint(input);
      }
      return false;
    }

    // Allow only one dot (decimal point)
    if (keyValue === '.' && input.value.indexOf('.') === -1) {
      return true;
    }

    // Allow only digits
    if (!/^[0-9]$/.test(keyValue)) {
      return false;
    }

    // Check for decimal places
    const valueAfterInput = this.getValueAfterInput(
      input.value,
      keyValue,
      input.selectionStart
    );
    if (this.hasExceededDecimalPlaces(valueAfterInput)) {
      return false;
    }

    return true;
  }

  // Covers sources that never fire keypress: mobile keyboards, autofill and
  // programmatic writes. Any comma is rewritten as a decimal point.
  @HostListener('input') public onInput() {
    const input = this.el.nativeElement;
    if (!input.value.includes(',')) {
      return;
    }

    const cursorPosition = input.selectionStart;
    input.value = this.replaceCommaWithDecimalPoint(input.value);
    input.setSelectionRange?.(cursorPosition, cursorPosition);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  @HostListener('paste', ['$event']) public onPaste(event: ClipboardEvent) {
    const pastedText = this.replaceCommaWithDecimalPoint(
      event.clipboardData?.getData('text') ?? ''
    );
    const input = this.el.nativeElement;
    const valueAfterPaste = this.getValueAfterInput(
      input.value,
      pastedText,
      input.selectionStart
    );

    if (
      !this.isValidNumber(valueAfterPaste) ||
      this.hasExceededDecimalPlaces(valueAfterPaste)
    ) {
      event.preventDefault();
    }
  }

  private isControlKey(event: KeyboardEvent): boolean {
    const controlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Enter',
    ];
    return controlKeys.includes(event.key);
  }

  private getValueAfterInput(
    currentValue: string,
    inputValue: string,
    cursorPosition: number
  ): string {
    const valueBeforeCursor = currentValue.substring(0, cursorPosition);
    const valueAfterCursor = currentValue.substring(cursorPosition);
    return valueBeforeCursor + inputValue + valueAfterCursor;
  }

  private replaceCommaWithDecimalPoint(value: string): string {
    return value.replace(/,/g, '.');
  }

  private insertDecimalPoint(input: HTMLInputElement): void {
    const cursorPosition = input.selectionStart ?? input.value.length;
    input.value = this.getValueAfterInput(input.value, '.', cursorPosition);
    input.setSelectionRange?.(cursorPosition + 1, cursorPosition + 1);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private isValidNumber(value: string): boolean {
    return /^-?\d*\.?\d*$/.test(value);
  }

  private hasExceededDecimalPlaces(value: string): boolean {
    if (!value.includes('.')) {
      return false;
    }
    const decimalPart = value.split('.')[1];
    return decimalPart.length > this.decimalPlaces!;
  }
}
